/**
 * TrainingPage — Training Programs & Learning Management
 * Tabs: All Programs | My Training | (HR) Manage
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Row, Col, Button, Badge, Modal,
    Form, Spinner, Table, ProgressBar
} from 'react-bootstrap';
import { BookOpen, Plus, Users, CheckCircle, Clock, Edit2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const HR_ROLES = ['super-admin','admin','hr-manager','account-manager'];
const STATUS_COLOUR = { upcoming:'primary', ongoing:'warning', completed:'success', cancelled:'secondary' };
const MODE_ICON     = { online:'💻', offline:'🏢', hybrid:'🔄' };

const fmtDate = (d) => {
    if (!d) return '—';
    const [y,m,dd] = String(d).slice(0,10).split('-').map(Number);
    return new Date(y,m-1,dd).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'});
};

const TrainingPage = () => {
    const { user }  = useAuth();
    const isHR      = HR_ROLES.includes(user?.role);
    const [tab, setTab] = useState('all');

    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1">
                        <BookOpen size={22} className="me-2 text-primary"/>Training & Learning
                    </h4>
                    <p className="text-muted small mb-0">Programs · Enrollments · Progress</p>
                </div>
            </div>
            <div className="d-flex gap-1 mb-4">
                {[
                    { key:'all',    label:'All Programs' },
                    { key:'my',     label:'My Training'  },
                    ...(isHR ? [{ key:'manage', label:'Manage' }] : []),
                ].map(t => (
                    <Button key={t.key} size="sm"
                        variant={tab===t.key ? 'primary' : 'outline-secondary'}
                        onClick={() => setTab(t.key)}>
                        {t.label}
                    </Button>
                ))}
            </div>
            {tab === 'all'    && <AllTrainingsTab isHR={isHR}/>}
            {tab === 'my'     && <MyTrainingTab/>}
            {tab === 'manage' && isHR && <ManageTab/>}
        </div>
    );
};

// ── All Programs Tab ──────────────────────────────────────────────────────────
const AllTrainingsTab = ({ isHR }) => {
    const [trainings, setTrainings] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [filter,    setFilter]    = useState('');

    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/trainings').then(r => setTrainings(r.data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load, []);

    const enroll = async (id) => {
        try { await axiosInstance.post(`/performance/trainings/${id}/enroll`, {}); toast.success('Enrolled!'); load(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    };

    const filtered = trainings.filter(t =>
        !filter || t.title?.toLowerCase().includes(filter.toLowerCase())
    );

    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary"/></div>;

    return (
        <>
            <div className="d-flex gap-2 mb-3">
                <Form.Control size="sm" placeholder="Search programs…" style={{ maxWidth:260 }}
                    value={filter} onChange={e => setFilter(e.target.value)}/>
                {['upcoming','ongoing','completed'].map(s => (
                    <Button key={s} size="sm"
                        variant={filter===s?'primary':'outline-secondary'}
                        onClick={() => setFilter(filter===s?'':s)}>
                        {s.charAt(0).toUpperCase()+s.slice(1)}
                    </Button>
                ))}
            </div>
            <Row className="g-3">
                {filtered.length === 0 && (
                    <Col xs={12}>
                        <Card className="border-0 shadow-sm p-5 text-center text-muted">
                            <BookOpen size={40} className="mx-auto mb-2 opacity-30"/>
                            <p>No training programs found.</p>
                        </Card>
                    </Col>
                )}
                {filtered.map(t => (
                    <Col key={t.id} xs={12} md={6} lg={4}>
                        <Card className="border-0 shadow-sm h-100 p-4">
                            <div className="d-flex justify-content-between mb-2">
                                <Badge bg={STATUS_COLOUR[t.status]||'secondary'}>{t.status}</Badge>
                                <span className="text-muted small">{MODE_ICON[t.mode]} {t.mode}</span>
                            </div>
                            <h6 className="fw-bold mb-1">{t.title}</h6>
                            {t.trainer && <div className="text-muted small mb-1">By {t.trainer}</div>}
                            {t.description && (
                                <p className="text-muted small mb-2" style={{ fontSize:12, lineHeight:1.5 }}>
                                    {t.description.slice(0,100)}{t.description.length>100?'…':''}
                                </p>
                            )}
                            <div className="d-flex gap-3 mb-3 small text-muted">
                                <span><Clock size={12} className="me-1"/>{fmtDate(t.start_date)}</span>
                                <span><Users size={12} className="me-1"/>{t.enrolled_count||0} enrolled</span>
                            </div>
                            {(t.skills_covered||[]).length > 0 && (
                                <div className="d-flex flex-wrap gap-1 mb-3">
                                    {(t.skills_covered||[]).slice(0,3).map((s,i) => (
                                        <Badge key={i} bg="light" text="dark" style={{ fontSize:10 }}>{s}</Badge>
                                    ))}
                                </div>
                            )}
                            {t.status !== 'cancelled' && (
                                <Button size="sm" variant="outline-primary" className="mt-auto"
                                    onClick={() => enroll(t.id)}>
                                    Enroll →
                                </Button>
                            )}
                        </Card>
                    </Col>
                ))}
            </Row>
        </>
    );
};

// ── My Training Tab ───────────────────────────────────────────────────────────
const MyTrainingTab = () => {
    const [myTrainings, setMy] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axiosInstance.get('/performance/trainings/my').then(r => setMy(r.data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
    }, []);

    const completed = myTrainings.filter(t => t.enrollment_status === 'completed').length;
    const enrolled  = myTrainings.filter(t => t.enrollment_status === 'enrolled').length;

    if (loading) return <div className="text-center py-4"><Spinner animation="border"/></div>;

    return (
        <>
            <Row className="g-3 mb-4">
                {[
                    { label:'Enrolled',  val:enrolled,  color:'#4f46e5' },
                    { label:'Completed', val:completed, color:'#059669' },
                    { label:'Total',     val:myTrainings.length, color:'#0891b2' },
                ].map((k,i) => (
                    <Col key={i} xs={4}>
                        <Card className="border-0 shadow-sm p-3 text-center">
                            <div style={{ fontSize:28, fontWeight:800, color:k.color }}>{k.val}</div>
                            <div className="text-muted small">{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>
            {myTrainings.length === 0 ? (
                <Card className="border-0 shadow-sm p-4 text-center text-muted">
                    <p>You haven't enrolled in any training programs yet.</p>
                </Card>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize:10 }}>
                                <th className="ps-4">Program</th>
                                <th>Mode</th>
                                <th>Dates</th>
                                <th>Status</th>
                                <th className="pe-4">Score</th>
                            </tr>
                        </thead>
                        <tbody>
                            {myTrainings.map(t => (
                                <tr key={t.id}>
                                    <td className="ps-4 fw-medium">{t.title}</td>
                                    <td>{MODE_ICON[t.mode]} {t.mode}</td>
                                    <td>{fmtDate(t.start_date)}{t.end_date ? ` → ${fmtDate(t.end_date)}` : ''}</td>
                                    <td>
                                        <Badge bg={t.enrollment_status==='completed'?'success':t.enrollment_status==='dropped'?'secondary':'primary'} style={{ fontSize:10 }}>
                                            {t.enrollment_status}
                                        </Badge>
                                    </td>
                                    <td className="pe-4">{t.score != null ? `${t.score}%` : '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};

// ── Manage Tab (HR) ───────────────────────────────────────────────────────────
const ManageTab = () => {
    const [trainings, setTrainings] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [showForm,  setShowForm]  = useState(false);
    const [editing,   setEditing]   = useState(null);
    const [detailId,  setDetailId]  = useState(null);

    const load = () => {
        setLoading(true);
        axiosInstance.get('/performance/trainings').then(r => setTrainings(r.data.data||[])).catch(()=>{}).finally(()=>setLoading(false));
    };
    useEffect(load, []);

    const del = async (id) => {
        if (!window.confirm('Delete this training?')) return;
        try { await axiosInstance.delete(`/performance/trainings/${id}`); toast.success('Deleted'); load(); }
        catch { toast.error('Failed'); }
    };

    if (loading) return <div className="text-center py-4"><Spinner animation="border"/></div>;

    return (
        <>
            <div className="d-flex justify-content-between mb-3">
                <span className="text-muted small">{trainings.length} programs</span>
                <Button size="sm" variant="primary" onClick={() => { setEditing(null); setShowForm(true); }}>
                    <Plus size={14} className="me-1"/>New Program
                </Button>
            </div>
            <Card className="border-0 shadow-sm">
                <Table hover responsive className="mb-0 small align-middle">
                    <thead className="bg-light">
                        <tr className="text-muted text-uppercase" style={{ fontSize:10 }}>
                            <th className="ps-4">Title</th>
                            <th>Trainer</th>
                            <th>Dates</th>
                            <th>Enrolled</th>
                            <th>Status</th>
                            <th className="pe-4">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trainings.map(t => (
                            <tr key={t.id}>
                                <td className="ps-4 fw-medium">{t.title}</td>
                                <td>{t.trainer||'—'}</td>
                                <td>{fmtDate(t.start_date)}{t.end_date?` → ${fmtDate(t.end_date)}`:''}</td>
                                <td>
                                    <span className="fw-bold">{t.enrolled_count||0}</span>
                                    {t.max_participants ? <span className="text-muted">/{t.max_participants}</span> : ''}
                                </td>
                                <td>
                                    <Badge bg={STATUS_COLOUR[t.status]||'secondary'} style={{ fontSize:10 }}>
                                        {t.status}
                                    </Badge>
                                </td>
                                <td className="pe-4">
                                    <div className="d-flex gap-1">
                                        <Button size="sm" variant="outline-secondary" className="py-0"
                                            onClick={() => { setEditing(t); setShowForm(true); }}>
                                            <Edit2 size={12}/>
                                        </Button>
                                        <Button size="sm" variant="outline-danger" className="py-0"
                                            onClick={() => del(t.id)}>
                                            <Trash2 size={12}/>
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card>
            <TrainingFormModal show={showForm} training={editing}
                onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }}/>
        </>
    );
};

const TrainingFormModal = ({ show, training, onClose, onDone }) => {
    const isNew = !training?.id;
    const [form, setForm] = useState({ title:'', trainer:'', mode:'online', start_date:'', end_date:'', max_participants:'', description:'', status:'upcoming' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (training) setForm({
            title: training.title||'', trainer: training.trainer||'', mode: training.mode||'online',
            start_date: training.start_date?.slice?.(0,10)||'',
            end_date: training.end_date?.slice?.(0,10)||'',
            max_participants: training.max_participants||'',
            description: training.description||'', status: training.status||'upcoming',
        });
        else setForm({ title:'', trainer:'', mode:'online', start_date:'', end_date:'', max_participants:'', description:'', status:'upcoming' });
    }, [training, show]);

    const save = async () => {
        if (!form.title.trim() || !form.start_date) return toast.error('Title and start date required');
        setSaving(true);
        try {
            if (isNew) await axiosInstance.post('/performance/trainings', form);
            else       await axiosInstance.put(`/performance/trainings/${training.id}`, form);
            toast.success(isNew ? 'Program created!' : 'Updated!'); onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">{isNew ? 'New Training Program' : 'Edit Program'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}><Form.Label className="small fw-bold">Title *</Form.Label>
                        <Form.Control value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))}/></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Trainer</Form.Label>
                        <Form.Control value={form.trainer} onChange={e => setForm(p=>({...p,trainer:e.target.value}))}/></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Mode</Form.Label>
                        <Form.Select value={form.mode} onChange={e => setForm(p=>({...p,mode:e.target.value}))}>
                            <option value="online">Online</option><option value="offline">Offline</option><option value="hybrid">Hybrid</option>
                        </Form.Select></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Start Date *</Form.Label>
                        <Form.Control type="date" value={form.start_date} onChange={e => setForm(p=>({...p,start_date:e.target.value}))}/></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">End Date</Form.Label>
                        <Form.Control type="date" value={form.end_date} onChange={e => setForm(p=>({...p,end_date:e.target.value}))}/></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Max Participants</Form.Label>
                        <Form.Control type="number" min={1} value={form.max_participants} onChange={e => setForm(p=>({...p,max_participants:e.target.value}))}/></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Status</Form.Label>
                        <Form.Select value={form.status} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                            <option value="upcoming">Upcoming</option><option value="ongoing">Ongoing</option>
                            <option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                        </Form.Select></Col>
                    <Col xs={12}><Form.Label className="small fw-bold">Description</Form.Label>
                        <Form.Control as="textarea" rows={3} value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))}/></Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving ? <Spinner size="sm"/> : (isNew ? 'Create' : 'Save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default TrainingPage;
